import { useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  canSee,
  canWrite,
  layoutNodes,
  nodeColour,
  stripNoteMarkup,
  type CanonicalDocument,
  type ColourRole,
  type Commentary,
  type DocNode,
  type InlineLine,
  type Role,
} from '@cca/corpus-shared';
import { ccaLandscapeLogo } from '@cca/ui';
import { ApiError, api } from '../api/client';
import { NoteComposer, type ComposerValues } from './NoteComposer';
import { BlockEditor } from './BlockEditor';
import { HistoryPanel } from './HistoryPanel';
import { DiffView, type DiffSelection } from './DiffView';
import { renderNoteBody } from './noteFormat';
import { nodeTokens, currentAnchor, setLineAnchor, posKey, parsePos } from './anchorUtil';

/**
 * The reading surface (ADR-006 / liturgy-poc.html). Two columns: the liturgy
 * reads down the left in a paper panel; the Commission's rationale sits in the
 * right margin, each gloss tethered to the exact element by a stable id. Display
 * numbers are COMPUTED here (rubrics = running integer, headings = §, elements
 * inherit their rubric's number) so a note never points at a number — only at an
 * id, and the cross-reference can't break.
 *
 * Commentary is already audience-filtered by the worker; the Audience chips are a
 * VIEW filter over what arrived (mainly for the Commission to preview each tier).
 * Reading view hides the margin + anchors for a clean copy.
 */

// A block's colour role paints TEXT (its rubric number and direction), so the
// gold role takes the theme's text tone, --color-accent-ink (AA on every light
// ground). The tether path further down stays on the gold itself: a graphic
// (cca-mjd8f; gold-text.test.ts pins both).
const COLOUR_VAR: Record<ColourRole, string> = {
  ink: 'var(--color-ink)',
  rubric: 'var(--color-rubric)',
  structural: 'var(--color-structural)',
  accent: 'var(--color-accent-ink)',
  muted: 'var(--color-muted)',
};

const KIND_LABEL: Record<Commentary['kind'], string> = {
  decision: 'Decision',
  rationale: 'Rationale',
  discussion: 'Discussion',
  open: 'Open question',
  logic: 'Logic',
  purpose: 'Purpose',
  flow: 'Flow',
  content: 'Content',
  sources: 'Sources',
  scripture: 'Scripture',
  origin: 'Origin',
};

type ViewMode = 'annotated' | 'reading';

/** Tiers are cumulative: commission ⊇ clergy ⊇ public. The "View as" control
 *  lets a higher tier preview what a lower one sees; you can only view at or
 *  below your own tier. */
const VIEW_TIER_ORDER: Role[] = ['commission', 'clergy', 'public'];
const VIEW_TIER_LABEL: Record<Role, string> = {
  commission: 'Commission',
  clergy: 'Clergy',
  public: 'Public',
};

/** Print modes: `clean` = liturgy only; `annotated` = working copy with rationale. */
export type PrintMode = 'clean' | 'annotated' | null;
/** Annotated layout: `notes` = numbered endnotes; `gloss` = notes in the margin. */
export type PrintLayout = 'notes' | 'gloss';

interface ReadingViewProps {
  doc: CanonicalDocument;
  /** Reader's tier — bounds the "View as" chips + whether authoring shows. */
  tier: Role;
  /** Re-fetch the document after an authoring change. */
  onReload: () => void | Promise<void>;
  /** Print mode: drop the chrome and render the service for PDF (clean | annotated). */
  printMode?: PrintMode;
  /** Annotated print layout (numbered notes vs margin gloss). */
  printLayout?: PrintLayout;
  /** Tier the printed copy is filtered to (the export's "View as"). */
  printAs?: Role | undefined;
  /** In print mode, auto-open the browser print dialog once rendered. */
  autoPrint?: boolean;
}

/** Authoring composer target. `ref` is the auto-assigned, rubric-tethered citation. */
type Composer =
  | { mode: 'new'; targetNodeId: string; ref: string; init: ComposerValues }
  | { mode: 'edit'; targetNodeId: string; cid: string; ref: string; init: ComposerValues };

export function ReadingView({
  doc,
  tier,
  onReload,
  printMode = null,
  printLayout = 'notes',
  printAs,
  autoPrint = false,
}: ReadingViewProps): JSX.Element {
  const printClean = printMode === 'clean';
  const printAnnotated = printMode === 'annotated';
  const printing = printMode !== null;
  const [view, setView] = useState<ViewMode>(printClean ? 'reading' : 'annotated');

  // Client-side print: once the view has rendered (fonts + the logo image loaded),
  // open the browser's print dialog so the user can Save as PDF. This needs no
  // Browser Rendering — the user's own Chromium produces the same output.
  useEffect(() => {
    if (!(printing && autoPrint)) return;
    let cancelled = false;
    const imagesReady = Promise.all(
      Array.from(document.images).map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((res) => {
              img.onload = img.onerror = () => res();
            }),
      ),
    );
    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    void Promise.all([fontsReady, imagesReady]).then(() => {
      if (!cancelled) setTimeout(() => window.print(), 200);
    });
    return () => {
      cancelled = true;
    };
  }, [printing, autoPrint]);
  const [viewAs, setViewAs] = useState<Role>(printAs ?? tier);
  const [showIds, setShowIds] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  // Authoring is Commission-only (canWrite) AND only in the author's own
  // Annotated view — previewing a lower tier via "View as" is read-only.
  const editable = canWrite(tier) && view === 'annotated' && viewAs === tier;
  const [editingDoc, setEditingDoc] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Compare (visual diff) selection, opened from the History panel; null when closed.
  const [compare, setCompare] = useState<DiffSelection | null>(null);
  const [composer, setComposer] = useState<Composer | null>(null);
  const [saving, setSaving] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const docId = doc.document.id;

  const toggleExpand = (id: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const surfaceRef = useRef<HTMLElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const exportRef = useRef<HTMLDetailsElement>(null);

  // Close the Export menu (a native <details>) on an outside click or Escape —
  // native <details> only collapses on a re-click of its own summary.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const d = exportRef.current;
      if (d?.open && !d.contains(e.target as Node)) d.open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && exportRef.current) exportRef.current.open = false;
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Leave the History panel on a click anywhere outside it (incl. another toolbar
  // pill) or on Escape — no explicit "Done" needed. The History toggle button is
  // excluded so its own click handles open/close.
  useEffect(() => {
    if (!showHistory) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('.history-panel') || t.closest('.js-history-toggle')) return;
      setShowHistory(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowHistory(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showHistory]);

  // The Compare view is a full destination (not a transient popover), so it closes
  // on Escape only — not click-away — and returns the user to the History panel.
  useEffect(() => {
    if (!compare) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCompare(null);
        setShowHistory(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [compare]);

  // ---- document order + computed numbering -------------------------------
  // The SINGLE shared layout (tree-walk order + rubric numbers + indent depth),
  // also used by the Doc-builder export so the two renderers can't drift.
  const { ordered, byId, rubricNumberById, indentLevelById } = layoutNodes(doc.nodes);

  // ---- computed line references (tethered to the rubric) ------------------
  // h{n} header · r{n} rubric · r{N}-l{k} / r{N}-c{k} leader/congregation line
  // under rubric N. Leader = spoken-type, Congregation = response/prayer-type.
  const lineLetter = (t: DocNode['type']): 'l' | 'c' =>
    t === 'response' || t === 'prayer' ? 'c' : 'l';
  const nodeRefById = new Map<string, string>();
  {
    let headerN = 0;
    let subN = 0;
    let topL = 0;
    let topC = 0;
    const childCount = new Map<string, { l: number; c: number }>();
    for (const n of ordered) {
      if (n.type === 'heading') {
        nodeRefById.set(n.id, `h${++headerN}`);
      } else if (n.type === 'rubric') {
        nodeRefById.set(n.id, `r${rubricNumberById.get(n.id) ?? 0}`);
        childCount.set(n.id, { l: 0, c: 0 });
      } else if (n.type === 'sub-rubric') {
        // No number; cited as sr{n}. Lines nested under it still tether to the
        // nearest numbered rubric ancestor (the walk below skips sub-rubrics).
        nodeRefById.set(n.id, `sr${++subN}`);
      } else {
        const letter = lineLetter(n.type);
        // Tether to the nearest RUBRIC ancestor, not just the direct parent — a
        // Congregation response may now nest under a Leader prompt, which itself
        // sits under the rubric. The citation stays rubric-relative (r{N}-c{k}).
        let anc: string | null = n.parentId;
        let rubricId: string | null = null;
        while (anc) {
          if (rubricNumberById.has(anc)) {
            rubricId = anc;
            break;
          }
          anc = byId.get(anc)?.parentId ?? null;
        }
        if (rubricId) {
          const parentR = rubricNumberById.get(rubricId) ?? 0;
          const cc = childCount.get(rubricId) ?? { l: 0, c: 0 };
          cc[letter] += 1;
          childCount.set(rubricId, cc);
          nodeRefById.set(n.id, `r${parentR}-${letter}${cc[letter]}`);
        } else {
          const next = letter === 'l' ? (topL += 1) : (topC += 1);
          nodeRefById.set(n.id, `${letter}${next}`);
        }
      }
    }
  }

  // ---- structural indent (a response nested under a leader steps in) ------
  // `indentLevelById` (leader-ancestor depth) comes from the shared layout above.
  const indentStyle = (id: string): React.CSSProperties | undefined => {
    const level = indentLevelById.get(id) ?? 0;
    return level ? { marginLeft: `${2.2 + level * 1.6}rem` } : undefined;
  };

  // Blocks a note may be tethered to (label = its ref + a text snippet), and the
  // set of live node ids — a note whose target isn't here is "unanchored".
  const liveNodeIds = new Set(byId.keys());
  const nodeSnippet = (n: DocNode): string =>
    n.lines
      .map((l) => l.runs.map((r) => r.text).join(''))
      .join(' ')
      .trim();
  const targetOptions = ordered.map((n) => ({
    id: n.id,
    label: `${nodeRefById.get(n.id) || '—'} · ${nodeSnippet(n).slice(0, 40) || '(empty block)'}`,
  }));

  // ---- commentary indexes ------------------------------------------------
  const liveComments = doc.commentary.filter((c) => !c.deleted);
  // Heads = top-level notes (own anchor + gloss). Thread entries (relatesTo set)
  // are prior discussion shown collapsed under their head, never as anchors.
  const headComments = liveComments.filter((c) => !c.relatesTo);
  const threadByHead = new Map<string, Commentary[]>();
  for (const c of liveComments) {
    if (!c.relatesTo) continue;
    const list = threadByHead.get(c.relatesTo) ?? [];
    list.push(c);
    threadByHead.set(c.relatesTo, list);
  }
  for (const list of threadByHead.values())
    list.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const orderIndexOf = new Map(ordered.map((n, i) => [n.id, i]));
  const glosses = [...headComments].sort(
    (a, b) =>
      (orderIndexOf.get(a.targetNodeId) ?? 0) - (orderIndexOf.get(b.targetNodeId) ?? 0) ||
      a.createdAt.localeCompare(b.createdAt),
  );

  // Per-note reference = the line's reference; multiple notes on one line get a
  // trailing letter (r1-l2a, r1-l2b) so every note is uniquely citable.
  const noteRefById = new Map<string, string>();
  {
    const perNode = new Map<string, Commentary[]>();
    for (const c of headComments) {
      const list = perNode.get(c.targetNodeId) ?? [];
      list.push(c);
      perNode.set(c.targetNodeId, list);
    }
    for (const [nodeId, list] of perNode) {
      const base = nodeRefById.get(nodeId) ?? '?';
      list.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      list.forEach((c, i) =>
        noteRefById.set(c.id, list.length > 1 ? `${base}${String.fromCharCode(97 + i)}` : base),
      );
    }
  }
  const noteRef = (id: string) => noteRefById.get(id) ?? '';
  // Every block the active note is pinned to lights up (home + extra anchors).
  const activeComment = active ? headComments.find((c) => c.id === active) : undefined;
  const litTargets = activeComment
    ? new Set([activeComment.targetNodeId, ...activeComment.extraTargets])
    : null;

  /** Word-anchored head ids referenced by a node's runs. */
  const anchorIdsInNode = (node: DocNode): Set<string> => {
    const ids = new Set<string>();
    for (const line of node.lines)
      for (const run of line.runs) if (run.anchorId) ids.add(run.anchorId);
    return ids;
  };
  /**
   * Does this note appear on `nodeId`? — its home target OR one of its extra
   * (multi-anchor) targets. The same note body renders at every block it's pinned
   * to; a word-level marker only ever lives at the home node, so extra targets
   * always show the note node-level (line-end).
   */
  const noteOnNode = (c: Commentary, nodeId: string): boolean =>
    c.targetNodeId === nodeId || c.extraTargets.includes(nodeId);
  /** Node-level (non-word) heads for a node = pinned here, not a word anchor here. */
  const nodeLevelComments = (node: DocNode): Commentary[] => {
    const wordIds = anchorIdsInNode(node);
    return headComments.filter((c) => noteOnNode(c, node.id) && !wordIds.has(c.id));
  };
  // Cumulative: viewing "as clergy" shows clergy + public; "as commission" shows
  // everything the server already returned. canSee is the shared role→audience map.
  const visible = (c: Commentary) => canSee(viewAs, c.audience);
  const viewTierOptions = VIEW_TIER_ORDER.slice(VIEW_TIER_ORDER.indexOf(tier)).map((t) => ({
    v: t,
    label: VIEW_TIER_LABEL[t],
  }));

  const select = useCallback((id: string) => {
    setActive((cur) => (cur === id ? null : id));
  }, []);

  // On select, ALIGN the source text and its rationale note to the same vertical
  // band so the tether reads as a (near) horizontal line. The margin is sticky
  // (its gloss position is driven only by the margin's own scroll) and the
  // liturgy is driven only by the window — independent axes — so we can move each
  // to the band without them fighting. Falls back to a plain scroll-into-view on
  // mobile / reading mode (single column) or when the note has no live anchor.
  useEffect(() => {
    if (!active) return;
    const surface = surfaceRef.current;
    if (!surface) return;
    const gloss = surface.querySelector<HTMLElement>(`.gloss[data-note="${active}"]`);
    if (!gloss) return;

    const singleColumn = view === 'reading' || window.innerWidth <= 900;
    const anchor = surface.querySelector<HTMLElement>(`.rv-anchor[data-note="${active}"]`);
    const margin = surface.querySelector<HTMLElement>('.margin');
    if (singleColumn || !anchor || !margin) {
      gloss.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }

    const aTop = anchor.getBoundingClientRect().top;
    const gTop = gloss.getBoundingClientRect().top;
    // Target band: keep the source text put when it's already comfortably in view;
    // otherwise pull it into the upper-middle of the viewport.
    const band = Math.min(Math.max(aTop, 110), window.innerHeight * 0.5);
    // Liturgy follows the window; rationale follows the margin's own scrollTop.
    window.scrollBy({ top: aTop - band, behavior: 'smooth' });
    margin.scrollTo({ top: margin.scrollTop + (gTop - band), behavior: 'smooth' });
  }, [active, view]);

  // ---- authoring (Commission only) ---------------------------------------
  const closeComposer = () => {
    setComposer(null);
    setComposerError(null);
  };
  const openNew = (targetNodeId: string) => {
    setComposerError(null);
    setComposer({
      mode: 'new',
      targetNodeId,
      ref: nodeRefById.get(targetNodeId) ?? '',
      init: {
        kind: 'rationale',
        audience: 'commission',
        body: '',
        span: '',
        anchor: '',
        target: targetNodeId,
        extraTargets: [],
        linkToNoteId: null,
      },
    });
  };
  const openEdit = (cmt: Commentary) => {
    setComposerError(null);
    const node = doc.nodes.find((n) => n.id === cmt.targetNodeId);
    const anchor = node ? posKey(currentAnchor(node, cmt.id)) : '';
    setComposer({
      mode: 'edit',
      targetNodeId: cmt.targetNodeId,
      cid: cmt.id,
      ref: noteRef(cmt.id),
      init: {
        kind: cmt.kind,
        audience: cmt.audience,
        body: cmt.body,
        span: cmt.span ?? '',
        anchor,
        target: cmt.targetNodeId,
        extraTargets: [...cmt.extraTargets],
        linkToNoteId: null,
      },
    });
  };

  const saveComposer = async (v: ComposerValues) => {
    if (!composer) return;
    setSaving(true);
    setComposerError(null);
    try {
      // Linking this block to an existing note: no new note, no marker reconcile —
      // just add this block to that note's anchor set (excluding its home target).
      if (composer.mode === 'new' && v.linkToNoteId) {
        const note = headComments.find((c) => c.id === v.linkToNoteId);
        if (note) {
          const next = [...new Set([...note.extraTargets, composer.targetNodeId])].filter(
            (x) => x !== note.targetNodeId,
          );
          await api.setCommentaryAnchors(docId, note.id, next);
          setActive(note.id);
        }
        await onReload();
        closeComposer();
        return;
      }

      let noteId: string;
      const retethered = composer.mode === 'edit' && v.target !== composer.targetNodeId;
      if (composer.mode === 'edit') {
        await api.updateCommentary(docId, composer.cid, {
          kind: v.kind,
          audience: v.audience,
          body: v.body,
          span: v.span || null,
          ...(retethered ? { targetNodeId: v.target } : {}),
        });
        noteId = composer.cid;
      } else {
        const created = await api.createCommentary(docId, {
          targetNodeId: composer.targetNodeId,
          kind: v.kind,
          audience: v.audience,
          body: v.body,
          span: v.span || null,
        });
        noteId = created.id;
      }

      // When re-tethered, strip the note's word-marker off the old block (if it
      // still exists) so no stray circle is left behind.
      if (retethered) {
        const oldNode = doc.nodes.find((n) => n.id === composer.targetNodeId);
        if (oldNode && posKey(currentAnchor(oldNode, noteId)) !== '') {
          await api.updateNode(docId, oldNode.id, {
            lines: setLineAnchor(oldNode.lines, null, noteId),
          });
        }
      }

      // Reconcile the circle's position on the effective target: bind it to the
      // chosen word (or release it to the line end) by rewriting that node's lines.
      const targetId = composer.mode === 'edit' ? v.target : composer.targetNodeId;
      const node = doc.nodes.find((n) => n.id === targetId);
      if (node) {
        const desired = parsePos(v.anchor);
        if (posKey(currentAnchor(node, noteId)) !== posKey(desired)) {
          await api.updateNode(docId, node.id, {
            lines: setLineAnchor(node.lines, desired, noteId),
          });
        }
      }

      // Multi-anchor: replace the note's set of additional blocks (edit mode only —
      // a new note's anchors are added by editing it). Exclude the home target.
      if (composer.mode === 'edit') {
        await api.setCommentaryAnchors(
          docId,
          composer.cid,
          v.extraTargets.filter((x) => x !== targetId),
        );
      }

      await onReload();
      closeComposer();
    } catch (e) {
      setComposerError(e instanceof ApiError ? e.message : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Build/update the living Google Doc (clean service text, kept in the Commission
  // Shared Drive, updated in place → Google's own revision history) and open it.
  // Open the tab synchronously first so the async POST doesn't trip the popup blocker.
  const [livingDocBusy, setLivingDocBusy] = useState(false);
  const openLivingDoc = async () => {
    const tab = window.open('', '_blank');
    setLivingDocBusy(true);
    try {
      const res = await fetch(`/api/documents/${encodeURIComponent(docId)}/living-doc`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const json = (await res.json().catch(() => ({}))) as { url?: string };
      if (res.ok && json.url && tab) tab.location.href = json.url;
      else {
        tab?.close();
        window.alert('Could not update the Google Doc. Please try again.');
      }
    } catch {
      tab?.close();
      window.alert('Could not update the Google Doc. Please try again.');
    } finally {
      setLivingDocBusy(false);
    }
  };

  const removeNote = async (cmt: Commentary) => {
    if (!window.confirm('Delete this note? Any earlier discussion under it is removed too.'))
      return;
    try {
      await api.deleteCommentary(docId, cmt.id);
      if (active === cmt.id) setActive(null);
      await onReload();
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : 'Could not delete.');
    }
  };

  // ---- tether ------------------------------------------------------------
  const updateTether = useCallback(() => {
    const path = pathRef.current;
    const surface = surfaceRef.current;
    if (!path || !surface) return;
    if (!active || view === 'reading' || window.innerWidth <= 900) {
      path.setAttribute('opacity', '0');
      return;
    }
    // A multi-anchor note has several markers sharing this data-note; draw a
    // curve from EACH of them to the single gloss (one path, multiple subpaths).
    const anchors = surface.querySelectorAll<HTMLElement>(`.rv-anchor[data-note="${active}"]`);
    const gloss = surface.querySelector<HTMLElement>(`.gloss[data-note="${active}"]`);
    if (anchors.length === 0 || !gloss) {
      path.setAttribute('opacity', '0');
      return;
    }
    const base = surface.getBoundingClientRect();
    const g = gloss.getBoundingClientRect();
    const x2 = g.left - base.left;
    const y2 = g.top - base.top + 18;
    let d = '';
    anchors.forEach((anchor) => {
      const a = anchor.getBoundingClientRect();
      const x1 = a.right - base.left;
      const y1 = a.top - base.top + a.height / 2;
      const mx = (x1 + x2) / 2;
      d += `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2} `;
    });
    path.setAttribute('d', d.trim());
    path.setAttribute('opacity', '0.65');
  }, [active, view]);

  useLayoutEffect(updateTether, [updateTether, viewAs, showIds]);
  useEffect(() => {
    let raf = 0;
    const onMove = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateTether);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [updateTether]);

  // ---- render a single node ---------------------------------------------
  const Anchor = ({ noteId, atWord }: { noteId: string; atWord?: boolean }) => (
    <span className="rv-anchor-wrap">
      <button
        type="button"
        className={`rv-anchor${atWord ? ' word' : ''}${active === noteId ? ' active' : ''}`}
        data-note={noteId}
        aria-label={`Note ${noteRef(noteId)}`}
        onClick={() => select(noteId)}
      />
    </span>
  );

  const exists = (anchorId: string) => headComments.some((c) => c.id === anchorId);

  /**
   * Render a node's lines with anchor markers. Markers sit at the END of the line
   * by default (clean reading); a run flagged `anchorAtWord` shows its marker
   * inline at the word instead. Node-level commentary markers append to the last
   * line. Either way the binding/tether target is the marker element.
   */
  const NodeLines = ({ node, bold }: { node: DocNode; bold: boolean }) => {
    const nodeLevel = nodeLevelComments(node);
    const lastLine = node.lines.length - 1;
    return (
      <>
        {node.lines.map((line: InlineLine, li) => {
          const lineEnd: string[] = [];
          for (const run of line.runs) {
            if (run.anchorId && !run.anchorAtWord && exists(run.anchorId))
              lineEnd.push(run.anchorId);
          }
          if (li === lastLine) for (const c of nodeLevel) lineEnd.push(c.id);
          return (
            <span key={li} className={`rv-line${bold || line.response ? ' resp' : ''}`}>
              <span className="rv-text">
                {line.runs.map((run, ri) => {
                  const style: React.CSSProperties = {};
                  if (run.bold) style.fontWeight = 700;
                  if (run.italic) style.fontStyle = 'italic';
                  const inlineId =
                    run.anchorId && run.anchorAtWord && exists(run.anchorId) ? run.anchorId : null;
                  return (
                    <span key={ri}>
                      <span style={style}>{run.text}</span>
                      {inlineId && <Anchor noteId={inlineId} atWord />}
                    </span>
                  );
                })}
                {lineEnd.map((id) => (
                  <Anchor key={id} noteId={id} />
                ))}
                {/* short id (first 8 of the UUID) — debug aid only, full id unchanged */}
                {showIds && li === lastLine && <span className="sid">{node.id.slice(0, 8)}</span>}
              </span>
              {/* One pill per line, hard-right at the column edge (not the text/child
                  edge); the note still tethers to the block. Revealed on line hover. */}
              {editable && (
                <button
                  type="button"
                  className="add-note"
                  title="Add a note here"
                  onClick={() => openNew(node.id)}
                >
                  + note
                </button>
              )}
            </span>
          );
        })}
      </>
    );
  };

  const renderNode = (node: DocNode): JSX.Element => {
    const litActive = litTargets?.has(node.id) ? ' lit-active' : '';
    if (node.type === 'note') {
      return (
        <p key={node.id} className={`note-fm node${litActive}`} data-id={node.id}>
          <NodeLines node={node} bold={false} />
        </p>
      );
    }
    if (node.type === 'heading') {
      return (
        <div key={node.id} className={`section node${litActive}`} data-id={node.id}>
          <h2>{node.lines.map((l) => l.runs.map((r) => r.text).join('')).join(' ')}</h2>
          {nodeLevelComments(node).map((c) => (
            <Anchor key={c.id} noteId={c.id} />
          ))}
          {showIds && <span className="sid">{node.id.slice(0, 8)}</span>}
          {editable && (
            <button
              type="button"
              className="add-note"
              title="Add a note here"
              onClick={() => openNew(node.id)}
            >
              + note
            </button>
          )}
        </div>
      );
    }
    if (node.type === 'rubric' || node.type === 'sub-rubric') {
      const sub = node.type === 'sub-rubric';
      return (
        <div
          key={node.id}
          className={`rubric${sub ? ' sub-rubric' : ''} node${litActive}`}
          data-id={node.id}
        >
          {/* Sub-rubric keeps the empty number cell so its direction stays on the
              same justified line as numbered rubrics, just without a number. */}
          <div className="n" style={{ color: COLOUR_VAR[nodeColour(node)] }} aria-hidden={sub}>
            {sub ? '' : rubricNumberById.get(node.id)}
          </div>
          <div className="dir" style={{ color: COLOUR_VAR[nodeColour(node)] }}>
            <NodeLines node={node} bold={false} />
          </div>
        </div>
      );
    }
    if (node.type === 'spoken' || node.type === 'response') {
      return (
        <p
          key={node.id}
          className={`said ${node.type === 'response' ? 'resp' : 'lead'} node${litActive}`}
          data-id={node.id}
          style={indentStyle(node.id)}
        >
          <NodeLines node={node} bold={node.type === 'response'} />
        </p>
      );
    }
    // prayer | spoken-block | alternative
    return (
      <div key={node.id} className={`block node${litActive}`} data-id={node.id}>
        {node.type === 'alternative' && node.label && (
          <p className="said alt-label">{node.label}</p>
        )}
        <NodeLines node={node} bold={false} />
      </div>
    );
  };

  const Seg = <T extends string>({
    value,
    set,
    options,
  }: {
    value: T;
    set: (v: T) => void;
    options: { v: T; label: string }[];
  }) => (
    <div className="seg" role="group">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          aria-pressed={value === o.v}
          onClick={() => {
            set(o.v);
            setActive(null);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );

  // ---- annotated print (working copy: service + tier-filtered rationale) ----
  // Visible head notes numbered 1..n in document order; markers in the text point
  // at the numbered entries (endnotes, or beside the block in the gloss layout).
  const visibleGlosses = glosses.filter(visible);
  const noteNumberById = new Map(visibleGlosses.map((c, i) => [c.id, i + 1]));
  const headsOnNode = (node: DocNode): Commentary[] =>
    headComments
      .filter((c) => noteOnNode(c, node.id) && visible(c))
      .sort((a, b) => (noteNumberById.get(a.id) ?? 0) - (noteNumberById.get(b.id) ?? 0));

  // Static lines (no interactive anchors/pills); markers append to the last line.
  const PrintLines = ({
    node,
    bold,
    markers,
  }: {
    node: DocNode;
    bold: boolean;
    markers?: JSX.Element | null;
  }) => {
    const last = node.lines.length - 1;
    return (
      <>
        {node.lines.map((line, li) => (
          <span key={li} className={`rv-line${bold || line.response ? ' resp' : ''}`}>
            <span className="rv-text">
              {line.runs.map((run, ri) => {
                const style: React.CSSProperties = {};
                if (run.bold) style.fontWeight = 700;
                if (run.italic) style.fontStyle = 'italic';
                return (
                  <span key={ri} style={style}>
                    {run.text}
                  </span>
                );
              })}
              {li === last && markers}
            </span>
          </span>
        ))}
      </>
    );
  };

  const nodeMarkers = (node: DocNode): JSX.Element | null => {
    const heads = headsOnNode(node);
    if (!heads.length) return null;
    return (
      <>
        {heads.map((c) => (
          <sup key={c.id} className="apr-mark">
            {noteNumberById.get(c.id)}
          </sup>
        ))}
      </>
    );
  };

  // A node's text with its note markers, reusing the service type styling.
  const printNode = (node: DocNode): JSX.Element => {
    const markers = nodeMarkers(node);
    if (node.type === 'heading') {
      return (
        <div key={node.id} className="section node">
          <h2>
            {node.lines.map((l) => l.runs.map((r) => r.text).join('')).join(' ')}
            {markers}
          </h2>
        </div>
      );
    }
    if (node.type === 'rubric' || node.type === 'sub-rubric') {
      const sub = node.type === 'sub-rubric';
      return (
        <div key={node.id} className={`rubric${sub ? ' sub-rubric' : ''} node`}>
          <div className="n" style={{ color: COLOUR_VAR[nodeColour(node)] }} aria-hidden={sub}>
            {sub ? '' : rubricNumberById.get(node.id)}
          </div>
          <div className="dir" style={{ color: COLOUR_VAR[nodeColour(node)] }}>
            <PrintLines node={node} bold={false} markers={markers} />
          </div>
        </div>
      );
    }
    if (node.type === 'spoken' || node.type === 'response') {
      return (
        <p
          key={node.id}
          className={`said ${node.type === 'response' ? 'resp' : 'lead'} node`}
          style={indentStyle(node.id)}
        >
          <PrintLines node={node} bold={node.type === 'response'} markers={markers} />
        </p>
      );
    }
    return (
      <div key={node.id} className={`${node.type === 'note' ? 'note-fm' : 'block'} node`}>
        <PrintLines node={node} bold={false} markers={markers} />
      </div>
    );
  };

  // One commentary entry — used in the endnotes list and the gloss margin.
  const printNoteEntry = (c: Commentary): JSX.Element => (
    <div key={c.id} className="apr-note">
      <span className="apr-num">{noteNumberById.get(c.id)}</span>
      <span className="apr-note-body">
        <span className="apr-kind">{KIND_LABEL[c.kind]}</span>
        {viewAs === 'commission' && <span className="apr-aud">{c.audience}</span>}
        <span className="apr-ref">{noteRef(c.id)}</span> {renderNoteBody(c.body)}
        {(c.author || c.createdAt) && (
          <span className="apr-attr">
            {' — '}
            {c.author ?? ''}
            {c.createdAt
              ? `${c.author ? ', ' : ''}${new Date(c.createdAt).toLocaleDateString('en-NZ', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}`
              : ''}
          </span>
        )}
      </span>
    </div>
  );

  // Document-level explanatory Notes (shown to all tiers), as a print section.
  const annotatedWorkingNotes = doc.document.notes;
  const hasWorkingNotes = annotatedWorkingNotes.some((l) =>
    l.runs.some((r) => r.text.trim() !== ''),
  );
  const printWorkingNotes = (): JSX.Element => (
    <section className="apr-worknotes">
      <h2 className="apr-endnotes-head">Notes</h2>
      {annotatedWorkingNotes.map((line, li) => (
        <p key={li} className="wn-line">
          {line.runs.length === 0
            ? ' '
            : line.runs.map((run, ri) => {
                const style: React.CSSProperties = {};
                if (run.bold) style.fontWeight = 700;
                if (run.italic) style.fontStyle = 'italic';
                return (
                  <span key={ri} style={style}>
                    {run.text}
                  </span>
                );
              })}
        </p>
      ))}
    </section>
  );

  const renderAnnotatedPrint = (): JSX.Element => {
    if (printLayout === 'gloss') {
      // Two parallel flowing columns via a single table row (which the browser
      // splits across pages): the service reads continuously on the left, the
      // notes flow on the right keyed by the superscript number — no per-block gaps.
      return (
        <main className="apr apr-gloss">
          <table className="apr-gloss-table">
            <tbody>
              <tr>
                <td className="apr-litcol">{ordered.map(printNode)}</td>
                <td className="apr-notescol">
                  {visibleGlosses.length === 0 ? (
                    <p className="apr-empty">No commentary visible at this tier.</p>
                  ) : (
                    visibleGlosses.map(printNoteEntry)
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          {hasWorkingNotes && printWorkingNotes()}
        </main>
      );
    }
    // numbered notes — service, then the Notes, then the numbered notes, each
    // starting on a fresh page (break-before) for easy reference when printed.
    return (
      <main className="apr apr-notes">
        <section className="apr-service">{ordered.map(printNode)}</section>
        {hasWorkingNotes && printWorkingNotes()}
        {visibleGlosses.length > 0 && (
          <section className="apr-endnotes">
            <h2 className="apr-endnotes-head">Rationale &amp; decisions</h2>
            {visibleGlosses.map(printNoteEntry)}
          </section>
        )}
      </main>
    );
  };

  // Explanatory notes shown below the service — visible to every tier and part of
  // the printed service (both the Annotated and clean Reader views), whenever
  // there's actual text. Edited in the block editor.
  const noteLines = doc.document.notes;
  const showNotes = noteLines.some((l) => l.runs.some((r) => r.text.trim() !== ''));

  return (
    <div className={`reading-root${view === 'reading' ? ' rv-reading' : ''}`}>
      {/* document toolbar — sits just under the org PortalHeader (hidden in print) */}
      {!printing && (
        <div className="doc-toolbar">
          <Link to="/read" className="back-link">
            ← All documents
          </Link>
          <div className="toolbar-controls">
            {editingDoc ? (
              <button
                type="button"
                className="chip-toggle on"
                onClick={() => {
                  // Editor saved everything in the background; refetch once so the
                  // reading surface reflects the latest before we show it again.
                  setEditingDoc(false);
                  void onReload();
                }}
              >
                Done editing
              </button>
            ) : (
              <>
                <span className="ctl-label">View</span>
                <Seg
                  value={view}
                  set={setView}
                  options={[
                    { v: 'annotated', label: 'Annotated' },
                    { v: 'reading', label: 'Reader' },
                  ]}
                />
                {viewTierOptions.length > 1 && (
                  <>
                    <span className="ctl-label">View as</span>
                    <Seg value={viewAs} set={setViewAs} options={viewTierOptions} />
                  </>
                )}
                <button
                  type="button"
                  className={`chip-toggle${showIds ? ' on' : ''}`}
                  aria-pressed={showIds}
                  onClick={() => setShowIds((s) => !s)}
                >
                  Show ids
                </button>
                {canWrite(tier) && (
                  <button
                    type="button"
                    className="chip-toggle"
                    onClick={() => {
                      setEditingDoc(true);
                      setShowHistory(false);
                      setActive(null);
                      setComposer(null);
                    }}
                  >
                    Edit document
                  </button>
                )}
                {canWrite(tier) && (
                  <button
                    type="button"
                    className={`chip-toggle js-history-toggle${showHistory ? ' on' : ''}`}
                    aria-pressed={showHistory}
                    onClick={() => {
                      setShowHistory((v) => !v);
                      setEditingDoc(false);
                      setActive(null);
                      setComposer(null);
                    }}
                  >
                    History
                  </button>
                )}
                {/* Export menu — each item opens the print-styled view in a new tab
                  and auto-triggers Save-as-PDF (client-side; no Browser Rendering
                  quota). Annotated copies carry the current "View as" tier. */}
                <details className="export-menu" ref={exportRef}>
                  <summary className="chip-toggle">Export ▾</summary>
                  <div className="export-menu-items">
                    <a
                      href={`/read/${encodeURIComponent(doc.document.id)}?print=clean&autoprint=1`}
                      target="_blank"
                      rel="noopener"
                    >
                      Clean service
                    </a>
                    <a
                      href={`/read/${encodeURIComponent(doc.document.id)}?print=annotated&layout=notes&as=${viewAs}&autoprint=1`}
                      target="_blank"
                      rel="noopener"
                    >
                      Annotated · numbered notes
                    </a>
                    {/* Margin-gloss export disabled for now — readability/breaks need
                        more work; the render path stays (?print=annotated&layout=gloss)
                        to return to alongside the GAS Doc-builder. */}

                    {/* Living Google Doc — Commission only. Clean service text kept
                        in the Commission Shared Drive, updated in place (revision
                        history); shared read-only with the Commission. */}
                    {canWrite(tier) && (
                      <>
                        <span className="export-menu-head">Living document</span>
                        <button type="button" disabled={livingDocBusy} onClick={openLivingDoc}>
                          {livingDocBusy ? 'Updating…' : 'Open in Google Docs ↗'}
                        </button>
                      </>
                    )}
                  </div>
                </details>
              </>
            )}
          </div>
        </div>
      )}

      <header className="masthead">
        {printing && <img className="print-logo" src={ccaLandscapeLogo} alt="CCAANZ" />}
        {!printing && <p className="eyebrow">Working draft · for discussion</p>}
        <h1>{doc.document.title}</h1>
        {doc.document.subtitle && <p className="sub">{doc.document.subtitle}</p>}
        {/* status/updated meta is editorial, not part of the printed service */}
        {!printing && (
          <p className="meta">
            {doc.document.status} · updated{' '}
            {new Date(doc.document.updatedAt).toLocaleDateString('en-NZ', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </p>
        )}
        {printAnnotated && (
          <p className="apr-caption">
            Working copy · {VIEW_TIER_LABEL[viewAs]} ·{' '}
            {printLayout === 'gloss' ? 'margin notes' : 'numbered notes'}
          </p>
        )}
      </header>

      {printAnnotated ? (
        renderAnnotatedPrint()
      ) : compare ? (
        <DiffView
          docId={docId}
          current={doc}
          selection={compare}
          onReload={onReload}
          onClose={() => {
            setCompare(null);
            setShowHistory(true);
          }}
        />
      ) : showHistory ? (
        <HistoryPanel
          docId={docId}
          onReload={onReload}
          onCompare={(sel) => {
            setCompare(sel);
            setShowHistory(false);
          }}
        />
      ) : editingDoc ? (
        <BlockEditor doc={doc} />
      ) : (
        <main className="surface" ref={surfaceRef}>
          <svg className="tether" aria-hidden="true">
            <path
              ref={pathRef}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="1.4"
              strokeDasharray="3 3"
              opacity="0"
            />
          </svg>
          <div className="grid">
            <section className="liturgy" aria-label="Liturgy">
              {ordered.map((n) => renderNode(n))}
            </section>
            <aside className="margin" aria-label="Rationale and decisions">
              <div className="mhead">Rationale &amp; decisions</div>

              {composer &&
                (() => {
                  const cNode = doc.nodes.find((n) => n.id === composer.targetNodeId);
                  // New note: existing notes this block may link to instead — every
                  // head note NOT already shown on this block, searchable by ref.
                  const linkOptions =
                    composer.mode === 'new'
                      ? headComments
                          .filter((c) => !noteOnNode(c, composer.targetNodeId))
                          .map((c) => ({
                            id: c.id,
                            ref: noteRef(c.id),
                            snippet: ((p) => (p.length > 60 ? `${p.slice(0, 60)}…` : p))(
                              stripNoteMarkup(c.body),
                            ),
                          }))
                      : [];
                  // Edit note: its current extra anchors, labelled by block reference.
                  const anchorLabels =
                    composer.mode === 'edit'
                      ? composer.init.extraTargets.map((id) => ({
                          id,
                          label: nodeRefById.get(id) || '—',
                        }))
                      : [];
                  return (
                    <NoteComposer
                      key={`${composer.mode}:${
                        composer.mode === 'edit' ? composer.cid : composer.targetNodeId
                      }`}
                      title={composer.mode === 'edit' ? 'Edit note' : 'New note'}
                      reference={composer.ref}
                      anchorOptions={cNode ? nodeTokens(cNode) : []}
                      targetOptions={composer.mode === 'edit' ? targetOptions : []}
                      linkOptions={linkOptions}
                      anchorLabels={anchorLabels}
                      init={composer.init}
                      saving={saving}
                      error={composerError}
                      onSave={saveComposer}
                      onCancel={closeComposer}
                    />
                  );
                })()}

              {glosses.length === 0 && !composer && <p className="empty">No commentary yet.</p>}
              {glosses.map((c) => {
                // Only earlier discussion the current viewer/audience can access —
                // if none, the expand prompt must not appear at all.
                const thread = (threadByHead.get(c.id) ?? []).filter(visible);
                const isOpen = expanded.has(c.id);
                return (
                  <div key={c.id} className="gloss-wrap">
                    <button
                      type="button"
                      className={`gloss${active === c.id ? ' active' : ''}${visible(c) ? '' : ' dim'}`}
                      data-note={c.id}
                      data-target={c.targetNodeId}
                      onClick={() => select(c.id)}
                    >
                      <span className="tags">
                        <span className={`kind ${c.kind}`}>{KIND_LABEL[c.kind]}</span>
                        <span className="aud">{c.audience}</span>
                        {!liveNodeIds.has(c.targetNodeId) && (
                          <span className="unanchored">unanchored</span>
                        )}
                        <span className="ref">
                          {noteRef(c.id)}
                          {c.span ? ` · ${c.span}` : ''}
                        </span>
                      </span>
                      {c.createdAt && (
                        <span className="date">
                          {new Date(c.createdAt).toLocaleDateString('en-NZ', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      )}
                      <span className="gbody">{renderNoteBody(c.body)}</span>
                    </button>

                    {editable && (
                      <div className="gloss-actions">
                        <button type="button" className="link-btn" onClick={() => openEdit(c)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="link-btn danger"
                          onClick={() => removeNote(c)}
                        >
                          Delete
                        </button>
                      </div>
                    )}

                    {thread.length > 0 && (
                      <button
                        type="button"
                        className="thread-toggle"
                        aria-expanded={isOpen}
                        onClick={() => toggleExpand(c.id)}
                      >
                        {isOpen
                          ? '− Hide earlier discussion'
                          : `+ Earlier discussion (${thread.length})`}
                      </button>
                    )}
                    {isOpen && (
                      <div className="thread">
                        {thread.map((t) => (
                          <div key={t.id} className="thread-entry">
                            <span className="tags">
                              <span className={`kind ${t.kind}`}>{KIND_LABEL[t.kind]}</span>
                              <span className="aud">{t.audience}</span>
                              {t.createdAt && (
                                <span className="ref">
                                  {new Date(t.createdAt).toLocaleDateString('en-NZ', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })}
                                </span>
                              )}
                            </span>
                            <span className="gbody">{renderNoteBody(t.body)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </aside>
          </div>
          {showNotes && !printAnnotated && (
            <section className="working-notes" aria-label="Notes">
              <h2 className="wn-head">Notes</h2>
              {noteLines.map((line, li) => (
                <p key={li} className="wn-line">
                  {line.runs.length === 0
                    ? ' '
                    : line.runs.map((run, ri) => {
                        const style: React.CSSProperties = {};
                        if (run.bold) style.fontWeight = 700;
                        if (run.italic) style.fontStyle = 'italic';
                        return (
                          <span key={ri} style={style}>
                            {run.text}
                          </span>
                        );
                      })}
                </p>
              ))}
            </section>
          )}
        </main>
      )}
    </div>
  );
}
